<template>
  <div class="lo-row" :class="{ 'lo-row--no-access': !hasAccess }" :style="{ borderLeftColor: borderColor }">
    <button class="lo-main" :disabled="!hasAccess" @click="onPlayClick">
      <div class="thumb" :class="{ 'thumb--no-access': !hasAccess }" :style="thumbStyle">
        <span v-if="hasAccess" class="play-overlay"><IconPlay /></span>
        <i v-else class="material-icons play-overlay no-access-icon">block</i>
        <div
          v-if="progressPercent > 0 && !isCompleted && hasAccess"
          class="progress"
          :style="{ width: progressPercent + '%', backgroundColor: $theme.primaryColor }"
        ></div>
      </div>
      <div class="info">
        <p class="name">{{ learningObject.name }}</p>
        <p class="status" :class="{ 'in-progress': isInProgress && hasAccess }" :style="(isInProgress && hasAccess) ? { color: $theme.primaryColor } : null">
          <span v-if="!hasAccess" class="status-icon">○</span>
          <span v-else-if="isCompleted" class="status-icon done">●</span>
          <span v-else-if="isInProgress" class="status-icon">◐</span>
          <span v-else class="status-icon">○</span>
          <span class="status-text">
            <template v-if="!hasAccess">Sem acesso</template>
            <template v-else-if="isInProgress && learningObject.duration">{{ formatSeconds(positionSeconds) }} / {{ formatSeconds(learningObject.duration) }}</template>
            <template v-else-if="learningObject.duration">{{ formatSeconds(learningObject.duration) }}</template>
            <template v-else-if="isCompleted">Concluída</template>
            <template v-else-if="isInProgress">Em andamento</template>
            <template v-else>Disponível offline</template>
            <template v-if="sizeLabel"> · {{ sizeLabel }}</template>
          </span>
        </p>
      </div>
    </button>
    <button class="dots-btn" aria-label="Mais ações" @click="$emit('open-actions', { enrollId, loId: learningObject.id, name: learningObject.name, hasAccess })">
      <span>⋮</span>
    </button>
  </div>
</template>

<script>
import IconPlay from '@/components/UI/IconPlay.vue';
import { formatBytes } from '@/services/download/downloadHelpers';

function pad(n) { return n < 10 ? '0' + n : '' + n; }

export default {
  name: 'DownloadedLearningObjectRow',
  components: { IconPlay },
  props: {
    learningObject: { type: Object, required: true },
    enrollId: { type: [String, Number], required: true },
    positionSeconds: { type: Number, default: 0 },
    // Propagado pela árvore (Card → GroupRow → aqui). false quando o enroll
    // do curso foi removido pelo backend (matrícula revogada). UI:
    //   - Card desabilitado (botão `disabled`, sem feedback de click)
    //   - Ícone block sobre o thumb
    //   - Texto "Sem acesso" no lugar de duração/progresso
    //   - Botão ⋮ continua disponível pra remoção via ActionsSheet
    hasAccess: { type: Boolean, default: true }
  },
  computed: {
    isCompleted() {
      const { duration } = this.learningObject;
      if (!duration) return false;
      return this.positionSeconds >= duration - 3;
    },
    isInProgress() {
      return this.positionSeconds > 0 && !this.isCompleted;
    },
    progressPercent() {
      const { duration } = this.learningObject;
      if (!duration || !this.positionSeconds) return 0;
      return Math.min(100, Math.round((this.positionSeconds / duration) * 100));
    },
    thumbStyle() {
      if (this.learningObject.thumbnail) {
        return { backgroundImage: `url("${this.learningObject.thumbnail}")` };
      }
      return { backgroundColor: '#1a1f2e' };
    },
    sizeLabel() {
      return this.learningObject.size ? formatBytes(this.learningObject.size) : '';
    },
    borderColor() {
      const c = this.$theme.primaryColor || '#ffffff';
      // Mesma técnica do header: rgba a partir do hex pra deixar a linha-de-tempo
      // sutil sem variar SCSS por tema.
      if (!c.startsWith('#')) return c;
      const clean = c.replace('#', '');
      const full = clean.length === 3 ? clean.split('').map(x => x + x).join('') : clean;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.2)`;
    }
  },
  methods: {
    onPlayClick() {
      // hasAccess=false → o button já tem :disabled, mas guard defensivo aqui
      // cobre o caso de algum styling sobrescrever (ex: tema custom). Emit
      // só pra hasAccess=true; MyDownloads tem segundo gate pra defesa em
      // profundidade.
      if (!this.hasAccess) return;
      this.$emit('play', { enrollId: this.enrollId, loId: this.learningObject.id });
    },
    formatSeconds(s) {
      const total = Math.max(0, Math.floor(s || 0));
      const m = Math.floor(total / 60);
      const sec = total % 60;
      return `${m}:${pad(sec)}`;
    }
  }
};
</script>

<style scoped lang="scss">
.lo-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 8px 12px;
  margin-left: 8px;
  border-left: 1.5px solid;

  // Curso sem acesso: linha inteira desaturada, nome em cinza. Botão ⋮
  // continua interativo (cor não muda) — user precisa dele pra remover.
  &--no-access {
    .name {
      color: var(--md-text-dim);
    }
  }
}

.lo-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  padding: 4px 0;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
}

.thumb {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 32px;
  background-size: cover;
  background-position: center;
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--md-thumb-empty);

  // Thumb desaturada pra reforçar visualmente que a aula não pode ser tocada.
  &--no-access {
    filter: grayscale(1);
    opacity: 0.5;
  }
}

.no-access-icon {
  font-size: 14px;
  color: rgba(239, 68, 68, 0.9);
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;

  // IconPlay crava :stroke=primaryColor no SVG; força branco só aqui (igual ao Continue assistindo).
  ::v-deep svg g { stroke: #fff; }
  ::v-deep svg { width: 10px; height: 10px; }
}

.progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  max-width: 100%;
  // Zera a margin do `.progress` global do Materialize (margin-bottom empurrava
  // a barra absolute(bottom:0) pra cima do thumbnail).
  margin: 0;
}

.info {
  flex: 1;
  min-width: 0;
}

.name {
  margin: 0 0 2px;
  font-size: 12px;
  font-weight: 500;
  color: var(--md-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status {
  margin: 0;
  font-size: 11px;
  color: var(--md-text-dim);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;

  &.in-progress {
    font-weight: 600;
  }
}

.status-icon {
  font-size: 14px;
  line-height: 1;

  &.done { color: #1cb86f; }
}

.status-text {
  display: inline-flex;
  align-items: baseline;
}

.dots-btn {
  flex-shrink: 0;
  width: 40px;
  background: transparent;
  border: none;
  color: var(--md-text-dim);
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;

  &:active { color: var(--md-text); }
}
</style>
